import { Vector3 } from "three";
import type { Mesh } from "three";
import { ShadingScene, colormaps } from "@openpv/simshady";
import type { GeoJSONFeature } from "mapbox-gl";
import * as SunCalc from "suncalc";
import {
  featuresToBufferGeometry,
  featuresToShadingMeshes,
  getGeometryShift,
  getIrradianceUrl,
  isPointInSun,
  type LocalOrigin,
  restoreLocalShift,
  transformMeshesToRaycastSpace,
} from "./geometryUtils";
import {
  buildSimulationGeometryForTarget,
  buildSimulationGeometryFromFeatures,
} from "./simshadySceneGeometry";
import type SimShadyMeshLayer from "./SimShadyMeshLayer";

export interface ShadeProfileOptions {
  location: { lat: number; lng: number };
  startDate: Date;
  endDate: Date;
  sunColor?: number[];
  shadeColor?: number[];
  tzId?: string;
  panelAltitude?: number;
  panelAzimuth?: number;
}

export interface ShadeProfileResult {
  data: Uint8ClampedArray;
}

const DEFAULT_SUN_COLOR = [255, 255, 255, 255];
const DEFAULT_SHADE_COLOR = [0, 0, 0, 255];

export async function generateLocationShadeProfile(
  features: GeoJSONFeature[],
  options: ShadeProfileOptions,
): Promise<ShadeProfileResult> {
  const {
    location,
    startDate,
    endDate,
    sunColor = DEFAULT_SUN_COLOR,
    shadeColor = DEFAULT_SHADE_COLOR,
  } = options;

  const origin: LocalOrigin = {
    lng: location.lng,
    lat: location.lat,
  };

  const baseGeometry = featuresToBufferGeometry(features, origin);
  const shift = baseGeometry
    ? getGeometryShift(baseGeometry)
    : new Vector3(0, 0, 0);

  const shadingMeshes = transformMeshesToRaycastSpace(
    featuresToShadingMeshes(features, origin),
    shift,
  );

  const start = startDate <= endDate ? startDate : endDate;
  const end = endDate >= startDate ? endDate : startDate;
  const rangeMs = end.getTime() - start.getTime();
  const stepMinutes = rangeMs > 7 * 24 * 60 * 60 * 1000 ? 60 : 1;
  const sampleCount = Math.max(
    1,
    Math.floor(rangeMs / (stepMinutes * 60 * 1000)) + 1,
  );
  const data = new Uint8ClampedArray(sampleCount * 4);

  for (let sample = 0; sample < sampleCount; sample++) {
    const current = new Date(
      start.getTime() + sample * stepMinutes * 60 * 1000,
    );
    const sunPosition = SunCalc.getPosition(
      current,
      location.lat,
      location.lng,
    );
    const inSun =
      sunPosition.altitude > 0 &&
      isPointInSun(
        shadingMeshes,
        origin,
        location.lng,
        location.lat,
        sunPosition.altitude,
        sunPosition.azimuth,
      );

    const color = inSun ? sunColor : shadeColor;
    const offset = sample * 4;
    data[offset] = color[0];
    data[offset + 1] = color[1];
    data[offset + 2] = color[2];
    data[offset + 3] = color[3];
  }

  return { data };
}

export function countSunMinutesFromProfile(
  data: Uint8ClampedArray,
  sunColor: number[] = DEFAULT_SUN_COLOR,
): number {
  let total = 0;
  for (let i = 0; i < data.length; i += 4) {
    if (
      data[i] === sunColor[0] &&
      data[i + 1] === sunColor[1] &&
      data[i + 2] === sunColor[2] &&
      data[i + 3] === sunColor[3]
    ) {
      total++;
    }
  }
  return total;
}

export interface SimshadyCalculationResult {
  sunHours: number;
  mesh: Mesh;
  origin: LocalOrigin;
}

export interface CalculateSunHoursOptions {
  meshLayer?: SimShadyMeshLayer | null;
  onProgress?: (percent: number) => void;
  /** When set, only these features are simulated (others provide shading). */
  simulationFeatures?: GeoJSONFeature[];
  shadingFeatures?: GeoJSONFeature[];
}

export function getFeatureContentId(feature: GeoJSONFeature): string | null {
  const contentId = feature.properties?.contentId;
  if (contentId !== undefined && contentId !== null) {
    return String(contentId);
  }
  if (feature.id !== undefined && feature.id !== null) {
    return String(feature.id);
  }
  return null;
}

export function isSameShadingFeature(
  a: GeoJSONFeature,
  b: GeoJSONFeature,
): boolean {
  const idA = getFeatureContentId(a);
  const idB = getFeatureContentId(b);
  return Boolean(idA && idB && idA === idB);
}

export function splitTargetAndShadingFeatures(
  targetFeature: GeoJSONFeature,
  allFeatures: GeoJSONFeature[],
): {
  simulationFeatures: GeoJSONFeature[];
  shadingFeatures: GeoJSONFeature[];
} {
  return {
    simulationFeatures: [targetFeature],
    shadingFeatures: allFeatures.filter(
      (feature) => !isSameShadingFeature(feature, targetFeature),
    ),
  };
}

function sunHoursFromIntensities(
  intensities: Float32Array,
  validTimesteps: number,
): number {
  const averageIntensity =
    Array.from(intensities).reduce((sum, value) => sum + value, 0) /
    intensities.length;
  const maxYield = 1400 * 0.15;
  const sunFraction = Math.max(0, Math.min(1, averageIntensity / maxYield));
  return (validTimesteps / 60) * sunFraction;
}

export async function runSimshadyCalculation(
  features: GeoJSONFeature[],
  location: { lat: number; lng: number },
  options: CalculateSunHoursOptions = {},
): Promise<SimshadyCalculationResult | null> {
  const origin: LocalOrigin = {
    lng: location.lng,
    lat: location.lat,
  };

  options.onProgress?.(0);

  const { simulation: simulationGeometry, contextShading } =
    options.simulationFeatures
      ? buildSimulationGeometryForTarget(
          options.simulationFeatures,
          options.shadingFeatures ?? [],
          origin,
        )
      : buildSimulationGeometryFromFeatures(features, origin);

  if (!simulationGeometry) {
    return null;
  }

  const shift = getGeometryShift(simulationGeometry);

  options.onProgress?.(5);

  const scene = new ShadingScene();
  scene.addSimulationGeometry(simulationGeometry);
  if (contextShading) {
    scene.addShadingGeometry(contextShading);
  }

  const irradianceUrl = getIrradianceUrl(location.lat, location.lng);
  await scene.addSolarIrradianceFromURL(irradianceUrl);
  options.onProgress?.(10);
  const c0 = [0, 0, 0.2];
  const c1 = [1, 0.2, 0.1];
  const c2 = [1, 1, 0.1];

  scene.addColorMap(
    colormaps.interpolateThreeColors({
      c0: c0 as [number, number, number],
      c1: c1 as [number, number, number],
      c2: c2 as [number, number, number],
    }),
  );

  const resultMesh = await scene.calculate({
    progressCallback: (progress, total) => {
      if (total <= 0) return;
      const calcPercent = 10 + Math.round((progress / total) * 85);
      options.onProgress?.(Math.min(calcPercent, 95));
    },
  });
  const intensities = resultMesh.geometry.getAttribute("intensities")?.array as
    | Float32Array
    | undefined;
  const validTimesteps =
    scene.solarIrradiance?.[0]?.metadata.valid_timesteps_for_aggregation ??
    8760;

  restoreLocalShift(simulationGeometry, shift);
  contextShading?.dispose();
  simulationGeometry.dispose();

  if (!intensities || intensities.length === 0) {
    resultMesh.geometry.dispose();
    if (Array.isArray(resultMesh.material)) {
      resultMesh.material.forEach((material) => material.dispose());
    } else {
      resultMesh.material.dispose();
    }
    return null;
  }

  const sunHours = sunHoursFromIntensities(intensities, validTimesteps);

  // Hand off the simshady-colored mesh to the Mapbox custom Three.js layer.
  if (options.meshLayer) {
    options.meshLayer.setMesh(resultMesh, origin);
  }

  return {
    sunHours,
    mesh: resultMesh,
    origin,
  };
}

export async function runSimshadyVisualization(
  features: GeoJSONFeature[],
  location: { lat: number; lng: number },
  options: {
    meshLayer: SimShadyMeshLayer;
    onProgress?: (percent: number) => void;
  },
): Promise<void> {
  await runSimshadyCalculation(features, location, {
    meshLayer: options.meshLayer,
    onProgress: options.onProgress,
  });
}

export async function calculateAnnualSunHoursWithSimshady(
  features: GeoJSONFeature[],
  location: { lat: number; lng: number },
  options: CalculateSunHoursOptions = {},
): Promise<number> {
  const result = await runSimshadyCalculation(features, location, options);
  if (result) {
    return result.sunHours;
  }

  const profileFeatures = options.simulationFeatures ?? features;
  const start = new Date(new Date().getFullYear(), 0, 1);
  const end = new Date(new Date().getFullYear() + 1, 0, 1);
  const profile = await generateLocationShadeProfile(profileFeatures, {
    location,
    startDate: start,
    endDate: end,
  });
  return countSunMinutesFromProfile(profile.data) / 60;
}

export async function calculateAnnualSunHoursForFeature(
  targetFeature: GeoJSONFeature,
  allFeatures: GeoJSONFeature[],
  location: { lat: number; lng: number },
  options: Omit<CalculateSunHoursOptions, "simulationFeatures" | "shadingFeatures"> = {},
): Promise<number> {
  const { simulationFeatures, shadingFeatures } = splitTargetAndShadingFeatures(
    targetFeature,
    allFeatures,
  );

  return calculateAnnualSunHoursWithSimshady(simulationFeatures, location, {
    ...options,
    simulationFeatures,
    shadingFeatures,
  });
}

export async function calculatePolygonSunHours(
  features: GeoJSONFeature[],
  openRing: number[][],
  stepLng = 0.00002,
  stepLat?: number,
  options: CalculateSunHoursOptions = {},
): Promise<number> {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;

  for (const [lng, lat] of openRing) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }

  const dLng = Math.max(Math.abs(stepLng), 1e-7);
  const dLat = Math.max(Math.abs(stepLat ?? stepLng), 1e-7);

  let total = 0;
  let count = 0;

  for (let lat = minLat; lat <= maxLat + 1e-12; lat += dLat) {
    for (let lng = minLng; lng <= maxLng + 1e-12; lng += dLng) {
      if (!pointInPolygon(openRing, lng, lat)) continue;
      total += await calculateAnnualSunHoursWithSimshady(features, {
        lat,
        lng,
      }, options);
      count++;
    }
  }

  return count > 0 ? total : 0;
}

function pointInPolygon(openRing: number[][], lng: number, lat: number) {
  let inside = false;
  for (let i = 0, j = openRing.length - 1; i < openRing.length; j = i++) {
    const [xi, yi] = openRing[i];
    const [xj, yj] = openRing[j];
    const intersect =
      yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}
